/**
 * Landing.jsx — the home page `/` (V2, approved mockup v2/landing)
 * ────────────────────────────────────────────────────────────────
 * Static marketing page: no auth, no backend calls. Its only inputs are the
 * locale (localStorage.locale via i18n), the URL hash (in-page anchors land
 * through App's ScrollToTop → scrollToHash) and static data.
 * Sections live in src/v2/landing/*, styles in src/v2/landing/landing.css
 * (every rule nested under `.pg-landing`).
 *
 * Anchor ids sit on each section's panel or heading (#how, #demo, #scan, #meet, #why,
 * #business, #reviews, #faq) so a link lands that element 16px under the bar;
 * #pricing is the price question (scrollToHash opens it); #top is the hero.
 */

import React from 'react';
import PublicHeader from '../components/PublicHeader.jsx';
import SiteFooter from '../components/SiteFooter.jsx';
import Seo from '../components/Seo.jsx';
import FaqSection from '../components/FaqSection.jsx';
import { HOME_FAQS } from '../seo/content.js';
import { useStr } from '../v2/landing/useStr.js';
import { Hero, HeroStage } from '../v2/landing/Hero.jsx';
import HowItWorks from '../v2/landing/HowItWorks.jsx';
import LiveDemo from '../v2/landing/LiveDemo.jsx';
import ScanSection from '../v2/landing/ScanSection.jsx';
import MeetSection from '../v2/landing/MeetSection.jsx';
import WhyWallet from '../v2/landing/WhyWallet.jsx';
import BusinessSection from '../v2/landing/BusinessSection.jsx';
import Reviews from '../v2/landing/Reviews.jsx';
import FinalCta from '../v2/landing/FinalCta.jsx';
import '../v2/landing/landing.css';

/** The phone/tablet menu sheet links of the approved mockup (desktop links = PublicHeader defaults). */
function useMenuLinks(t) {
  return [
    { key: 'how', label: t('menuHow'), to: '#how' },
    { key: 'demo', label: t('menuDemo'), to: '#demo' },
    { key: 'scan', label: t('menuScan'), to: '#scan' },
    { key: 'meet', label: t('menuMeet'), to: '#meet' },
    { key: 'business', label: t('menuBiz'), to: '/business' },
    { key: 'pricing', label: t('menuPricing'), to: '/pricing' },
    { key: 'faq', label: t('menuFaq'), to: '#faq' }
  ];
}

export default function Landing() {
  const { t, locale } = useStr();
  const menuLinks = useMenuLinks(t);
  return (
    <div className="pg-landing" data-lang={locale}>
      <Seo route="home" />
      <PublicHeader menuLinks={menuLinks} />
      <main>
        <Hero />
        <HeroStage />
        <HowItWorks />
        <LiveDemo />
        <ScanSection />
        <MeetSection />
        <WhyWallet />
        <BusinessSection />
        <Reviews />
        <FaqSection faqs={HOME_FAQS} id="faq" itemIds={{ 2: 'pricing' }} />
        <FinalCta />
      </main>
      <SiteFooter />
    </div>
  );
}
